<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
  symbol: { type: String, required: true },
  latest: { type: Object, required: true },
  indicators: { type: Object, required: true },
  ohlcv: { type: Array, default: () => [] },
});

const showReference = ref(false);
const range = ref('1Y'); // 1M,3M,6M,1Y,ALL

// Filter ohlcv based on range by index count (approximate window)
const filteredRows = computed(() => {
  const data = props.ohlcv || [];
  let count;
  switch (range.value) {
    case '1M':
      count = 22; // ~22 trading days
      break;
    case '3M':
      count = 66;
      break;
    case '6M':
      count = 132;
      break;
    case '1Y':
      count = 252;
      break;
    case 'ALL':
    default:
      return data;
  }
  return data.slice(-count);
});

// Price & volume series for top chart
const chartRows = computed(() => filteredRows.value);

const minPrice = computed(() => {
  const vals = chartRows.value.map(r => r.close).filter(v => v != null);
  if (!vals.length) return null;
  return Math.min(...vals);
});

const maxPrice = computed(() => {
  const vals = chartRows.value.map(r => r.close).filter(v => v != null);
  if (!vals.length) return null;
  return Math.max(...vals);
});

const normPrice = (price) => {
  if (minPrice.value == null || maxPrice.value == null) return 0.5;
  const span = maxPrice.value - minPrice.value || 1;
  return (price - minPrice.value) / span;
};

const maxVolume = computed(() => {
  const vals = chartRows.value.map(r => r.volume || 0);
  if (!vals.length) return 1;
  return Math.max(...vals) || 1;
});

const volumeTicks = computed(() => {
  const maxV = maxVolume.value;
  const step = maxV / 4 || 1;
  const ticks = [0, step, step * 2, step * 3, maxV];
  return ticks;
});

const priceTicks = computed(() => {
  if (minPrice.value == null || maxPrice.value == null) return [];
  const minP = minPrice.value;
  const maxP = maxPrice.value;
  const step = (maxP - minP) / 4 || 1;
  return [minP, minP + step, minP + step * 2, minP + step * 3, maxP];
});

// Tooltip copy from stock-tech-indicators-tooltip.txt (trimmed for brevity)
const tooltips = {
  support_resistance: {
    supports:
      'Likely price floors where buying interest may emerge, hinting potential bounce zones during past downtrends.',
    resistances:
      'Likely price ceilings where selling pressure intensifies, signaling possible pullbacks after rallies.',
    method:
      'Levels derived from fractal swing highs/lows, highlighting recurring structure-based support or resistance.',
  },
  sma: {
    20: 'Average closing price over 20 periods, gauging short-term trend and dynamic support/resistance.',
    50: 'Average closing price over 50 periods, tracking intermediate trend direction and momentum shifts.',
    200: 'Average closing price over 200 periods, indicating long-term trend bias and major support/resistance.',
  },
  ema: {
    12: 'Exponentially weighted 12-period average, reacting faster to price and capturing short-term trend changes.',
    26: 'Exponentially weighted 26-period average, smoothing swings while indicating mid-term trend direction.',
  },
  bollinger: {
    middle:
      'Middle band equals moving average; contextual anchor for volatility bands above and below, indicating mean.',
    upper:
      'Upper band marks statistically elevated prices, often acting as resistance during strong moves.',
    lower:
      'Lower band marks statistically depressed prices, often acting as support during declines.',
  },
  rsi: {
    14: '14‑period RSI measures momentum on a 0–100 scale; extremes can hint at potential reversals.',
  },
  macd: {
    macd: 'Difference between EMA12 and EMA26; tracks momentum shifts and bullish/bearish crossovers.',
    signal: 'Smoothed MACD line; crossovers with MACD generate entry/exit signals.',
    hist: 'MACD minus signal; visualizes momentum acceleration or deceleration.',
  },
  atr: {
    14: 'Average True Range over 14 periods; higher values mean higher volatility.',
  },
  stochastic: {
    k: 'Fast oscillator indicating close vs range; highlights overbought/oversold zones.',
    d: 'Smoothed %K; crossovers can refine timing versus raw %K.',
  },
  obv: {
    value:
      'On‑Balance Volume cumulatively sums volume to confirm or question price trends via divergences.',
  },
  volume: {
    raw: 'Latest traded shares; gauges immediate activity and liquidity today.',
    avg20: 'Average daily volume over the last 20 sessions; baseline for spotting spikes.',
  },
};

const referenceRows = [
  {
    indicator: 'RSI (14)',
    typical_range: '0 – 100',
    overbought_oversold: 'Overbought > 70, Oversold < 30',
    trade_trigger: 'Buy near 30, Sell near 70',
  },
  {
    indicator: 'MACD',
    typical_range: 'Around zero line',
    overbought_oversold: 'Bullish crossover above zero',
    trade_trigger: 'Buy on MACD > Signal, Sell opposite',
  },
  {
    indicator: 'Stochastic %K',
    typical_range: '0 – 100',
    overbought_oversold: 'Overbought > 80, Oversold < 20',
    trade_trigger: 'Buy near 20, Sell near 80',
  },
  {
    indicator: 'ATR (14)',
    typical_range: 'Varies by volatility',
    overbought_oversold: 'Higher ATR = high volatility',
    trade_trigger: 'Use for stop-loss sizing',
  },
  {
    indicator: 'Bollinger Bands',
    typical_range: 'Price usually within bands',
    overbought_oversold: 'Upper band ≈ overbought zone',
    trade_trigger: 'Buy near lower band, Sell near upper band',
  },
  {
    indicator: 'SMA (20)',
    typical_range: 'Price vs 20‑day average',
    overbought_oversold: 'Price far above ≈ overbought',
    trade_trigger: 'Buy if price crosses above SMA (20)',
  },
  {
    indicator: 'EMA (12/26)',
    typical_range: 'Price vs EMAs',
    overbought_oversold: 'EMA crossovers signal trend shifts',
    trade_trigger: 'Buy on EMA12 > EMA26, Sell reverse',
  },
  {
    indicator: 'OBV',
    typical_range: 'Rising or falling line',
    overbought_oversold: 'Divergences can foreshadow reversals',
    trade_trigger: 'Buy if OBV rises with price uptrend',
  },
  {
    indicator: 'Volume',
    typical_range: 'Compare to 20‑day avg',
    overbought_oversold: 'Spike = unusually strong interest',
    trade_trigger: 'Confirm breakouts with high volume vs avg',
  },
];

const tooltip = ref({ visible: false, x: 0, y: 0, lines: [] });
const hoveredIndex = ref(null);

const setTooltipForPrice = (idx, row, event) => {
  // Set tooltip position immediately on mouseenter
  if (event) {
    const rect = event.currentTarget.closest('.ti-chart-main').getBoundingClientRect();
    tooltip.value.x = event.clientX - rect.left + 8;
    tooltip.value.y = event.clientY - rect.top - 8;
  }
  hoveredIndex.value = idx;
  tooltip.value.lines = [
    `Date: ${row.date}`,
    `Open: ${row.open}`,
    `High: ${row.high}`,
    `Low: ${row.low}`,
    `Close: ${row.close}`,
  ];
  tooltip.value.visible = true;
};

const setTooltipForVolume = (idx, row, event) => {
  // Set tooltip position immediately on mouseenter
  if (event) {
    const rect = event.currentTarget.closest('.ti-chart-main').getBoundingClientRect();
    tooltip.value.x = event.clientX - rect.left + 8;
    tooltip.value.y = event.clientY - rect.top - 8;
  }
  hoveredIndex.value = idx;
  tooltip.value.lines = [
    `Date: ${row.date}`,
    `Volume: ${(row.volume || 0).toLocaleString('en-IN')}`,
  ];
  tooltip.value.visible = true;
};

const updateTooltipPosition = (event) => {
  if (!tooltip.value.visible) return;
  const chartMain = event.target.closest('.ti-chart-main');
  if (!chartMain) return;
  const rect = chartMain.getBoundingClientRect();
  tooltip.value.x = event.clientX - rect.left + 8;
  tooltip.value.y = event.clientY - rect.top - 8;
};

const hideTooltip = () => {
  tooltip.value.visible = false;
  hoveredIndex.value = null;
};
</script>

<template>
  <div class="ti-root">
    <div class="ti-header-row">
      <h3 class="ti-title">Technical indicators snapshot</h3>
      <span class="ti-symbol-pill">{{ symbol }}</span>
    </div>

    <!-- Range selector -->
    <div class="ti-range-row" v-if="ohlcv && ohlcv.length">
      <button
        v-for="opt in ['1M','3M','6M','1Y','ALL']"
        :key="opt"
        type="button"
        class="ti-range-btn"
        :class="{ 'ti-range-btn--active': range === opt }"
        @click="range = opt"
      >
        {{ opt === 'ALL' ? 'All' : opt }}
      </button>
    </div>

    <!-- Top chart: Price (line) + Volume (bars) with axes -->
    <div v-if="chartRows.length" class="ti-chart-shell">
      <div class="ti-chart-wrapper">
        <div class="ti-axis ti-axis--left">
          <div v-for="(v, i) in volumeTicks" :key="'v-' + i" class="ti-axis-tick">
            {{ Math.round(v / 1000) }}k
          </div>
          <div class="ti-axis-label">Volume</div>
        </div>
        <div
          class="ti-chart-main"
          @mouseleave="hideTooltip"
        >
          <div class="ti-chart-price">
            <svg class="ti-price-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
              <polyline
                v-if="chartRows.length"
                class="ti-price-line"
                :points="chartRows
                  .map((row, idx) => {
                    const x = (idx / Math.max(1, chartRows.length - 1)) * 100;
                    const y = 100 - normPrice(row.close) * 100;
                    return x.toFixed(2) + ',' + y.toFixed(2);
                  })
                  .join(' ')"
              />
            </svg>
            <div
              v-for="(row, idx) in chartRows"
              :key="'hp-' + row.date"
              class="ti-price-hotspot"
              :style="{
                left: (idx / Math.max(1, chartRows.length - 1)) * 100 + '%',
                bottom: (normPrice(row.close) * 100) + '%',
              }"
              @mouseenter="(e) => setTooltipForPrice(idx, row, e)"
              @mousemove="updateTooltipPosition"
            />
          </div>
          <div class="ti-chart-volume" :style="{ '--bars-count': chartRows.length }">
            <div
              v-for="(row, idx) in chartRows"
              :key="row.date"
              class="ti-volume-bar ti-volume-bar--top"
              :style="{
                height: ((row.volume || 0) / maxVolume * 100 || 0) + '%',
              }"
              @mouseenter="(e) => setTooltipForVolume(idx, row, e)"
              @mousemove="updateTooltipPosition"
            />
          </div>
          <!-- Move tooltip here, as last child of .ti-chart-main -->
          <div
            v-if="tooltip.visible && hoveredIndex !== null"
            class="ti-tooltip"
            :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }"
          >
            <div v-for="(line, i) in tooltip.lines" :key="i">{{ line }}</div>
          </div>
        </div>
        <div class="ti-axis ti-axis--right">
          <div v-for="(p, i) in priceTicks" :key="'p-' + i" class="ti-axis-tick">
            {{ p.toFixed(0) }}
          </div>
          <div class="ti-axis-label ti-axis-label--right">Price on NSE</div>
        </div>
      </div>
      <div class="ti-chart-legend">
        <span class="legend-item legend-item--price">Price on NSE</span>
        <span class="legend-item legend-item--volume">Volume</span>
      </div>
    </div>

    <!-- Technical indicators + Bollinger tables -->
    <div class="ti-indicators-row">
      <!-- 7-column technical indicators table -->
      <table class="ti-indicators-table">
        <thead>
          <tr>
            <th>
              <span>SMA 20</span>
              <span class="ti-info" :title="tooltips.sma[20]">i</span>
            </th>
            <th>
              <span>SMA 50</span>
              <span class="ti-info" :title="tooltips.sma[50]">i</span>
            </th>
            <th>
              <span>SMA 200</span>
              <span class="ti-info" :title="tooltips.sma[200]">i</span>
            </th>
            <th>
              <span>EMA 12</span>
              <span class="ti-info" :title="tooltips.ema[12]">i</span>
            </th>
            <th>
              <span>RSI (14)</span>
              <span class="ti-info" :title="tooltips.rsi[14]">i</span>
            </th>
            <th>
              <span>MACD</span>
              <span class="ti-info" :title="tooltips.macd.macd">i</span>
            </th>
            <th>
              <span>ATR (14)</span>
              <span class="ti-info" :title="tooltips.atr[14]">i</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{ indicators.sma['20'] != null ? indicators.sma['20'].toFixed(2) : '—' }}</td>
            <td>{{ indicators.sma['50'] != null ? indicators.sma['50'].toFixed(2) : '—' }}</td>
            <td>{{ indicators.sma['200'] != null ? indicators.sma['200'].toFixed(2) : '—' }}</td>
            <td>{{ indicators.ema['12'] != null ? indicators.ema['12'].toFixed(2) : '—' }}</td>
            <td>{{ indicators.rsi['14'] != null ? indicators.rsi['14'].toFixed(2) : '—' }}</td>
            <td>{{ indicators.macd.macd != null ? indicators.macd.macd.toFixed(2) : '—' }}</td>
            <td>{{ indicators.atr['14'] != null ? indicators.atr['14'].toFixed(2) : '—' }}</td>
          </tr>
        </tbody>
      </table>

      <!-- Bollinger Bands table -->
      <table class="ti-bollinger-table">
        <thead>
          <tr>
            <th colspan="3" class="center">Bollinger Bands</th>
          </tr>
          <tr>
            <th>
              <span>Middle</span>
              <span class="ti-info" :title="tooltips.bollinger.middle">i</span>
            </th>
            <th>
              <span>Upper</span>
              <span class="ti-info" :title="tooltips.bollinger.upper">i</span>
            </th>
            <th>
              <span>Lower</span>
              <span class="ti-info" :title="tooltips.bollinger.lower">i</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{ indicators.bollinger.middle != null ? indicators.bollinger.middle.toFixed(2) : '—' }}</td>
            <td>{{ indicators.bollinger.upper != null ? indicators.bollinger.upper.toFixed(2) : '—' }}</td>
            <td>{{ indicators.bollinger.lower != null ? indicators.bollinger.lower.toFixed(2) : '—' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <button class="ti-ref-toggle" type="button" @click="showReference = !showReference">
      <span v-if="showReference">Hide indicator reference</span>
      <span v-else>Show indicator reference</span>
    </button>

    <div v-if="showReference" class="ti-ref-panel">
      <table class="ti-ref-table">
        <thead>
          <tr>
            <th>Indicator</th>
            <th>Typical range</th>
            <th>Overbought / Oversold</th>
            <th>Typical buy / sell trigger</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in referenceRows" :key="row.indicator">
            <td>{{ row.indicator }}</td>
            <td>{{ row.typical_range }}</td>
            <td>{{ row.overbought_oversold }}</td>
            <td>{{ row.trade_trigger }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.ti-root {
  margin-top: 16px;
  padding: 16px;
  border-radius: 10px;
  border: 1px solid #e9ecef;
  background: #fff;
}

.ti-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.ti-title {
  margin: 0;
  font-size: 0.96rem;
}

.ti-symbol-pill {
  padding: 4px 10px;
  border-radius: 999px;
  background: #e3fafc;
  font-size: 0.78rem;
  font-weight: 700;
}

/* Range selector */
.ti-range-row {
  display: inline-flex;
  gap: 4px;
  margin-bottom: 6px;
}

.ti-range-btn {
  border-radius: 4px;
  border: 1px solid #e5e7eb;
  padding: 2px 8px;
  font-size: 0.75rem;
  background: #fff;
  cursor: pointer;
}

.ti-range-btn--active {
  border-color: #4c6fff;
  background: #eef2ff;
  color: #4c6fff;
}

/* Top chart */
.ti-chart-shell {
  margin-bottom: 12px;
}

.ti-chart-wrapper {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: stretch;
}

.ti-axis {
  display: flex;
  flex-direction: column-reverse; /* smallest at bottom, largest at top */
  justify-content: space-between;
  font-size: 0.7rem;
  color: #6b7280;
  padding: 4px 4px 0;
}

.ti-axis-tick {
  text-align: left;
}

.ti-axis--right .ti-axis-tick {
  text-align: right;
}

.ti-axis-label {
  margin-top: 2px;
}

.ti-axis-label--right {
  text-align: right;
}

.ti-chart-main {
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 20;
}

.ti-chart-price {
  position: relative;
  height: 220px;
  border-radius: 8px 8px 0 0;
  border: 1px solid #e9ecef;
  border-bottom: none;
  background: #ffffff;
  overflow: visible; /* was hidden, now visible so tooltip is not clipped */
}

.ti-price-svg {
  width: 100%;
  height: 100%;
}

.ti-price-line {
  fill: none;
  stroke: #4c6fff;
  stroke-width: 0.3; /* thinner line */
}

.ti-price-hotspot {
  position: absolute;
  transform: translate(-50%, 50%);
  width: 6px;
  height: 6px;
  cursor: pointer;
  pointer-events: all;
  background: rgba(76, 111, 255, 0.18); /* visible for debug */
  border: 1px solid #4c6fff; /* visible for debug */
  z-index: 10;
}

.ti-chart-volume {
  --bar-gap: 1px;
  height: 120px;
  border-radius: 0 0 8px 8px;
  border: 1px solid #e9ecef;
  border-top: none;
  display: flex;
  align-items: flex-end;
  gap: var(--bar-gap);
  padding: 0 0 4px;
  background: #f5f7ff;
}

.ti-volume-bar--top {
  flex: 0 0 calc((100% - (var(--bars-count) - 1) * var(--bar-gap)) / var(--bars-count));
  background: #a5b4fc;
}

.ti-chart-legend {
  display: flex;
  gap: 16px;
  margin-top: 4px;
  font-size: 0.75rem;
}

.legend-item::before {
  content: '';
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  margin-right: 4px;
}

.legend-item--price::before {
  background: #4c6fff;
}

.legend-item--volume::before {
  background: #a5b4fc;
}

/* Indicators tables */
.ti-indicators-row {
  display: flex;
  align-items: stretch;
  gap: 8px;
  margin-top: 12px;
}

.ti-indicators-table,
.ti-bollinger-table {
  border-collapse: collapse;
  font-size: 0.8rem;
}

.ti-indicators-table {
  flex: 1 1 auto;
}

.ti-indicators-table th,
.ti-indicators-table td,
.ti-bollinger-table th,
.ti-bollinger-table td {
  border: 1px solid #e9ecef;
  padding: 4px 8px;
  text-align: center;
}

.ti-indicators-table thead th {
  background: #f8f9fa;
}

.ti-bollinger-table thead th {
  background: #f8f9fa;
}

.ti-bollinger-table .center {
  text-align: center;
}

.ti-info {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  margin-left: 4px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 1px solid #adb5bd;
  font-size: 0.65rem;
  color: #495057;
  cursor: default;
}

/* Reference table */
.ti-ref-toggle {
  margin-top: 10px;
  border: 1px solid #dee2e6;
  background: #f8f9fa;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 0.78rem;
  cursor: pointer;
}

.ti-ref-panel {
  margin-top: 8px;
}

.ti-ref-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;
}

.ti-ref_table th,
.ti-ref-table td {
  border: 1px solid #e9ecef;
  padding: 4px 6px;
}

.ti-ref-table thead th {
  background: #f8f9fa;
}

@media (max-width: 900px) {
  .ti-indicators-row {
    flex-direction: column;
  }
}

.ti-price-hit {
  fill: transparent;
  cursor: pointer;
}

.ti-tooltip {
  position: absolute;
  pointer-events: none;
  background: rgba(17, 24, 39, 0.97);
  color: #f9fafb;
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  white-space: pre;
  transform: translate(8px, -8px);
  z-index: 9999;
  box-shadow: 0 2px 8px rgba(0,0,0,0.18);
  border: 1px solid #4c6fff;
}
</style>
